import { useFormik } from "formik";
import { TransactionTypeEnum, transactionTypeOptionList } from "../../common/enum/transaction-type-enum";
import { ITransactionDto } from "../../interfaces/transaction/ITransactionDto";
import { use, useEffect, useState } from "react";
import useLazyLoading from "../../common/hooks/loader";
import Loader from "../../components/loader";
import { useNotification } from "../../components/notification/useNotification";
import extractErrorMessage from "../../common/utils/errorMessages";
import { tryGetAllWalletList } from "../../services/wallet/wallet-service";
import IWalletDto from "../../interfaces/wallet/IWalletDto";
import { Button, FormHelperText, Grid, InputLabel, MenuItem, OutlinedInput, Select, SelectChangeEvent, Stack } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { CreateEditTransactionValidationSchema } from "./create-edit.validation";
import { tryGetCryptoCurrencyListByUser, tryGetStableCoinListByUser } from "../../services/crypto-currency/cripto-currency.service";
import { ICryptoCurrency } from "../../interfaces/crypto-currency/ICryptoCurrency";
import { v4 as uuidv4, validate } from 'uuid';
import { tryCreateTransaction, tryGetTransanctionById, tryUpdateTransaction } from "../../services/transaction/transaction.service";
import { useNavigate } from "react-router-dom";
import { getBaseURLWithAuth } from "../../common/utils/session";

interface Props {
    editMode: boolean;
    transactionId?: string;
}

export default function CreateEditTransactionForm(props: Props) {
    const { editMode, transactionId } = props;
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [walletList, setWalletList] = useState<IWalletDto[]>();
    const [cryptoCurrencyList, setCryptoCurrencyList] = useState<ICryptoCurrency[]>();
    const [stableCoinList, setStableCoinList] = useState<ICryptoCurrency[]>();
    const showLoading = useLazyLoading(loading);
    const { displayNotification } = useNotification();

    const initialValues: ITransactionDto = {
        id: '',
        transactionDate: new Date(""),
        transactionType: TransactionTypeEnum.Compra,
        cryptoCurrencyId: '',
        walletId: '',
        cryptoCurrencyQuantity: 0,
        fiatAmount: 0,
        price: 0
    }

    const formik = useFormik({
        initialValues,
        validationSchema: CreateEditTransactionValidationSchema,
        onSubmit: async (values, { setSubmitting }) => {
            const transaction: ITransactionDto = {
                id: editMode ? values.id : uuidv4(),
                transactionDate: values.transactionDate,
                transactionType: values.transactionType,
                cryptoCurrencyId: values.cryptoCurrencyId,
                walletId: values.walletId,
                cryptoCurrencyQuantity: values.cryptoCurrencyQuantity,
                fiatAmount: values.fiatAmount,
                price: values.price
            }

            if (values.stableCoinCurrencyId) {
                transaction.stableCoinCurrencyId = values.stableCoinCurrencyId;
            }

            try {
                setLoading(true);

                if (!editMode) {
                    const response = await tryCreateTransaction(transaction);

                    if (response?.status === 201) {
                        displayNotification({ type: 'success', message: 'Transação criada com sucesso' });
                    }
                }
                else {
                    const response = await tryUpdateTransaction(transaction);

                    if (response?.status === 200) {
                        displayNotification({ type: 'success', message: 'Transação editada com sucesso' });
                    }
                }
                navigate(getBaseURLWithAuth() + 'transaction');
            }
            catch (error) {
                displayNotification({ type: 'error', message: extractErrorMessage(error) })
                setSubmitting(false);
            }
            finally {
                setLoading(false);
            }
        }
        
    });

    async function getAllWalletList() {
        try {
            var response = await tryGetAllWalletList()

            if (response?.status === 200) {
                var walletList = response.data;
                setWalletList(walletList);
            }
        }
        catch (error) {
            displayNotification({ type: 'error', message: extractErrorMessage(error) });
        }
    }

    async function getCryptoCurrencyListByUser() {
        try {
            var response = await tryGetCryptoCurrencyListByUser();

            if (response?.status === 200) {
                var cryptoCurrencyList = response.data;
                setCryptoCurrencyList(cryptoCurrencyList);
            }
        }
        catch (error) {
            displayNotification({ type: 'error', message: extractErrorMessage(error) });
        }
    }

    async function getStableCoinListByUser() {
        try {
            var response = await tryGetStableCoinListByUser();

            if (response?.status === 200) {
                var stableCoinList = response.data;
                setStableCoinList(stableCoinList);
            }
        }
        catch (error) {
            displayNotification({ type: 'error', message: extractErrorMessage(error) });
        }
    }

    async function getTransactionById() {
        try {
            var response = await tryGetTransanctionById(transactionId!);

            if (response?.status === 200 && response.data) {
                updateFormValues(response.data);
            }
        }
        catch (error) {
            displayNotification({ type: 'error', message: extractErrorMessage(error) });
        }
        
    }

    useEffect(() => {
        setLoading(true);
        
        getAllWalletList();
        getCryptoCurrencyListByUser();
        getStableCoinListByUser();

        if (editMode && transactionId) {
            getTransactionById();
        }

        setLoading(false);
    }, [])

    function updateFormValues(transaction: ITransactionDto) {
        formik.setFieldValue('id', transaction.id);
        formik.setFieldValue('cryptoCurrencyId', transaction.cryptoCurrencyId);
        formik.setFieldValue('cryptoCurrencyQuantity', transaction.cryptoCurrencyQuantity);
        formik.setFieldValue('stableCoinCurrencyId', transaction.stableCoinCurrencyId);
        formik.setFieldValue('fiatAmount', transaction.fiatAmount);
        formik.setFieldValue('price', transaction.price);
        formik.setFieldValue('transactionDate', transaction.transactionDate);
        formik.setFieldValue('transactionType', transaction.transactionType);
        formik.setFieldValue('walletId', transaction.walletId);
    }

    return (
        <>
            {showLoading === true ? <Loader /> : false}
            <form noValidate onSubmit={formik.handleSubmit}>
                <Grid container spacing={3}>
                    <Grid sx={{ flex: 1 }}>
                        <Stack spacing={1} margin={3}>
                            <InputLabel id="transaction-date-label" required sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>
                                Data da Transação
                            </InputLabel>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    value={dayjs(formik.values.transactionDate)}
                                    onChange={(value) => formik.setFieldValue('transactionDate', value?.format("YYYY-MM-DD"))}
                                />
                            </LocalizationProvider>
                            {formik.touched.transactionDate && formik.errors.transactionDate && (
                                <FormHelperText error id="helper-text-date-error">
                                    {String(formik.errors.transactionDate)}
                                </FormHelperText>
                            )}
                        </Stack>
                    </Grid>
                    <Grid sx={{ flex: 1 }}>
                        <Stack spacing={1} margin={3}>
                            <InputLabel required id="transaction-type-label" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>
                                Tipo de Operação
                            </InputLabel>
                            <Select
                                labelId="transaction-type-label"
                                id="transaction-type-select"
                                value={formik.values.transactionType}
                                onBlur={formik.handleBlur}
                                onChange={(event) => formik.setFieldValue('transactionType', event.target.value)}
                                error={Boolean(formik.touched.transactionType && formik.errors.transactionType)}
                                MenuProps={{ disableScrollLock: true }}
                            >
                                {transactionTypeOptionList && transactionTypeOptionList.map((option) => (
                                    <MenuItem
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                            {formik.touched.transactionType && formik.errors.transactionType && (
                                <FormHelperText error id="helper-text-transaction-type-error">
                                    {formik.errors.transactionType}
                                </FormHelperText>
                            )}
                        </Stack>
                    </Grid>
                    <Grid sx={{ flex: 1 }}>
                        <Stack spacing={1} margin={3}>
                            <InputLabel id="wallet-select-label" required sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>
                                Carteira
                            </InputLabel>
                            <Select
                                labelId="wallet-select-label"
                                id="wallet-select"
                                value={formik.values.walletId}
                                onBlur={formik.handleBlur}
                                onChange={(event) => formik.setFieldValue('walletId', event.target.value)}
                                error={Boolean(formik.touched.walletId && formik.errors.walletId)}
                                MenuProps={{ disableScrollLock: true }}
                            >
                                {walletList && walletList.map((wallet) => (
                                    <MenuItem
                                        key={wallet.id}
                                        value={wallet.id}
                                    >
                                        {wallet.walletName}
                                    </MenuItem>
                                ))}
                            </Select>
                            {formik.touched.walletId && formik.errors.walletId && (
                                <FormHelperText error id="helper-text-select-wallet-error">
                                    {formik.errors.walletId}
                                </FormHelperText>
                            )}
                        </Stack>
                    </Grid>
                </Grid>
                <Grid container spacing={3}>
                     <Grid sx={{ flex: 1 }}>
                        <Stack spacing={1} margin={3}>
                            <InputLabel id="crypto-currency-label" required sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>
                                Cripto Moeda
                            </InputLabel>
                            <Select
                                labelId="crypto-currency-label"
                                id="crypto-currency-select"
                                value={formik.values.cryptoCurrencyId}
                                onBlur={formik.handleBlur}
                                onChange={(event) => formik.setFieldValue('cryptoCurrencyId', event.target.value)}
                                error={Boolean(formik.touched.cryptoCurrencyId && formik.errors.cryptoCurrencyId)}
                                MenuProps={{ disableScrollLock: true }}
                            >
                                {cryptoCurrencyList && cryptoCurrencyList.map((cryptoCurrency) => (
                                    <MenuItem
                                        key={cryptoCurrency.id}
                                        value={cryptoCurrency.id}
                                    >
                                        <Grid container alignItems="center" spacing={0.5}>
                                            <Grid>
                                                <img src={cryptoCurrency.imageUrl} width="20" height="20"/>
                                            </Grid>
                                            <Grid>
                                                {cryptoCurrency.name}
                                            </Grid>
                                        </Grid>
                                    </MenuItem>
                                ))}
                            </Select>
                            {formik.touched.cryptoCurrencyId && formik.errors.cryptoCurrencyId && (
                                <FormHelperText error id="helper-text-crypto-currency-error">
                                    {formik.errors.cryptoCurrencyId}
                                </FormHelperText>
                            )}
                        </Stack>
                    </Grid>
                    <Grid sx={{ flex: 1 }}>
                        <Stack spacing={1} margin={3}>
                            <InputLabel id="stable-coin-label" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>
                                Stable Coin
                            </InputLabel>
                            <Select
                                labelId="stable-coin-label"
                                id="stable-coin-select"
                                value={formik.values.stableCoinCurrencyId}
                                onBlur={formik.handleBlur}
                                onChange={(event) => formik.setFieldValue('stableCoinCurrencyId', event.target.value)}
                                MenuProps={{ disableScrollLock: true }}
                            >
                                {stableCoinList && stableCoinList.map((stableCoin) => (
                                    <MenuItem
                                        key={stableCoin.id}
                                        value={stableCoin.id}
                                    >
                                        <Grid container alignItems="center" spacing={0.5}>
                                            <Grid>
                                                <img src={stableCoin.imageUrl} width="20" height="20"/>
                                            </Grid>
                                            <Grid>
                                                {stableCoin.name}
                                            </Grid>
                                        </Grid>
                                    </MenuItem>
                                ))}
                            </Select>
                        </Stack>
                    </Grid>
                    <Grid sx={{ flex: 1 }}>
                        <Stack spacing={1} margin={3}>
                            <InputLabel htmlFor="crypto-currency-quantity" required sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>
                                Quantidade Cripto Moeda
                            </InputLabel>
                            <OutlinedInput
                                id="crypto-currency-quantity"
                                type="number"
                                onBlur={formik.handleBlur}
                                value={formik.values.cryptoCurrencyQuantity}
                                onChange={(event) => formik.setFieldValue('cryptoCurrencyQuantity', event.target.value)}
                                error={Boolean(formik.touched.cryptoCurrencyQuantity && formik.errors.cryptoCurrencyQuantity)}
                            />
                        
                            {formik.touched.cryptoCurrencyQuantity && formik.errors.cryptoCurrencyQuantity && (
                                <FormHelperText error id="helper-text-crypto-currency-quantity-error">
                                    {formik.errors.cryptoCurrencyQuantity}
                                </FormHelperText>
                            )}
                        </Stack>
                    </Grid>
                    <Grid sx={{ flex: 1 }}>
                        <Stack spacing={1} margin={3}>
                            <InputLabel htmlFor="fiat-amount" required sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>
                                Valor Moeda Fiduciária
                            </InputLabel>
                            <OutlinedInput
                                id="fiat-amount"
                                type="number"
                                onBlur={formik.handleBlur}
                                value={formik.values.fiatAmount}
                                onChange={(event) => formik.setFieldValue('fiatAmount', event.target.value)}
                                error={Boolean(formik.touched.fiatAmount && formik.errors.fiatAmount)}
                            />
                        
                            {formik.touched.fiatAmount && formik.errors.fiatAmount && (
                                <FormHelperText error id="helper-text-fiat-amount-error">
                                    {formik.errors.fiatAmount}
                                </FormHelperText>
                            )}
                        </Stack>
                    </Grid>
                </Grid>

                <Grid container spacing={3}>
                    <Grid sx={{flex: 1}}>
                        <Stack spacing={1} margin={3}>
                            <InputLabel htmlFor="price" required sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>
                                Preço da Cripto Moeda
                            </InputLabel>
                            <OutlinedInput
                                id="price"
                                type="number"
                                onBlur={formik.handleBlur}
                                value={formik.values.price}
                                onChange={(event) => formik.setFieldValue('price', event.target.value)}
                                error={Boolean(formik.touched.price && formik.errors.price)}
                            />
                        
                            {formik.touched.price && formik.errors.price && (
                                <FormHelperText error id="helper-text-price-error">
                                    {formik.errors.price}
                                </FormHelperText>
                            )}
                        </Stack>
                    </Grid>
                </Grid>
                <Grid margin={3}>
                    <Button
                        disableElevation
                        disabled={formik.isSubmitting}
                        fullWidth
                        size="large"
                        type="submit"
                        variant="contained"
                        color="primary"
                    >
                        Confirmar
                    </Button>
                </Grid>
            </form>
        </>
    )
}